import m from "mithril";
import CoordMobileLayout from "./Coord-mobile-layout.js";
import dataLayer from "../pwa/data-layer.js";
import { getTerm } from "../js/nomenclature.js";
import toast from "../js/toaster.js";

const CoordCustomerPortfolioMobile = {
    portfolios: [],
    loading: true,
    searching: false,
    searchTerm: '',
    searchDebounceId: null,
    
    // Pagination state (cursor-based)
    nextCursor: null,
    hasMore: false,
    limit: 20,

    async oninit() {
        await this.fetchPortfolios();
    },

    onremove() {
        if (this.searchDebounceId) {
            clearTimeout(this.searchDebounceId);
            this.searchDebounceId = null;
        }
    },

    async fetchPortfolios(append = false) {
        if (!append) {
            this.nextCursor = null;
            this.loading = true;
        }
        
        try {
            const response = await dataLayer.getCustomerPortfolios({
                search: this.searchTerm,
                limit: this.limit,
                cursor: append ? this.nextCursor : undefined
            });

            const items = response.items || [];

            if (append) {
                this.portfolios = [...this.portfolios, ...items];
            } else {
                this.portfolios = items;
            }

            const pagination = response.pagination || {};
            this.hasMore = pagination.hasMore || false;
            this.nextCursor = pagination.nextCursor || null;
        } catch (error) {
            console.error('[Portfolio] Fetch error:', error);
            toast.error('Gagal memuat data portfolio');
        } finally {
            this.loading = false;
            this.searching = false;
            m.redraw();
        }
    },

    handleSearch(value) {
        if (this.searchDebounceId) {
            clearTimeout(this.searchDebounceId);
        }

        this.searchTerm = value;
        this.searching = true;
        m.redraw();
        
        this.searchDebounceId = setTimeout(() => {
            this.searching = false;
            this.fetchPortfolios();
        }, 500);
    },

    async loadMore() {
        if (this.loading || !this.hasMore || !this.nextCursor) return;
        await this.fetchPortfolios(true);
    },

    getVisibleSavingsProducts(savings = []) {
        return savings.filter(acc => acc.has_account && acc.status === 'active');
    },

    getVisibleLoanProducts(loans = []) {
        return loans.filter(loan =>
            loan.has_application ||
            loan.loan_disbursement_id ||
            (parseFloat(loan.outstanding_amount) || 0) > 0 ||
            loan.is_disabled
        );
    },

    formatCurrency(amount) {
        if (!amount && amount !== 0) return amount;
        const num = parseFloat(amount);
        if (isNaN(num)) return amount;
        return `Rp ${num.toLocaleString('id-ID')}`;
    },

    renderPortfolioCard(p) {
        return m("div", {
            class: "bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 mb-3 active:scale-[0.98] transition-transform",
            onclick: () => m.route.set(`/coord-customer-portfolio-detail/${p.customer_id}`, { from: 'portfolio', returnTo: '/coord-customer-portfolio-mobile' })
        }, [
            m("div", { class: "flex items-center justify-between mb-3" }, [
                m("div", { class: "flex items-center gap-3" }, [
                    m("div", { class: "w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 font-bold" }, 
                        p.customer_name?.charAt(0)
                    ),
                    m("div", [
                        m("h3", { class: "font-bold text-gray-800 dark:text-white" }, p.customer_name),
                        m("p", { class: "text-xs text-gray-500" }, p.no_reg)
                    ])
                ]),
                m("i", { class: "ri-arrow-right-s-line text-gray-400" })
            ]),
            m("div", { class: "grid grid-cols-2 gap-4 border-t border-gray-50 dark:border-gray-700 pt-3" }, [
                m("div", [
                    m("p", { class: "text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1" }, "Simpanan"),
                    m("p", { class: "text-sm font-bold text-blue-600" }, this.formatCurrency(p.total_savings_balance))
                ]),
                m("div", [
                    m("p", { class: "text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1" }, "Pinjaman"),
                    m("p", { class: "text-sm font-bold text-red-600" }, this.formatCurrency(p.total_loan_outstanding))
                ])
            ])
        ]);
    },

    view(vnode) {
        const content = m("div", { class: "p-4 space-y-4" }, [
            // Search Header
            m("div", { class: "relative" }, [
                m("input", {
                    type: "text",
                    class: "w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-800 border-none rounded-2xl shadow-sm dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 transition-all",
                    placeholder: "Cari nama, no. reg, atau telepon...",
                    oninput: (e) => this.handleSearch(e.target.value)
                }),
                m("i", { class: "ri-search-2-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg" }),
                this.searching ? m("i", { class: "ri-loader-4-line absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-orange-500" }) : null
            ]),

            // List
            this.loading ? m("div", { class: "flex items-center justify-center py-20" }, [
                m("div", { class: "space-y-4 text-center" }, [
                    m("i", { class: "ri-loader-4-line animate-spin text-4xl text-orange-500 mx-auto" }),
                    m("p", { class: "text-gray-500" }, "Memuat daftar nasabah...")
                ])
            ]) : this.portfolios.length === 0 ? m("div", { class: "text-center py-20" }, [
                m("i", { class: "ri-user-search-line text-6xl text-gray-200 mb-4" }),
                m("p", { class: "text-gray-400" }, "Nasabah tidak ditemukan")
            ]) : m("div", { class: "pb-20" }, [
                this.portfolios.map(p => this.renderPortfolioCard(p)),
                
                this.hasMore ? m("button", {
                    class: "w-full py-3 text-sm text-gray-500 font-medium hover:text-orange-500 transition-colors",
                    onclick: () => this.loadMore()
                }, "Muat Lebih Banyak") : m("p", { class: "text-center text-xs text-gray-300 py-4" }, "Semua data telah dimuat")
            ])
        ]);

        if (vnode.attrs.isEmbedded) return content;
        return m(CoordMobileLayout, {
            title: "Portfolio Anggota",
            activeSection: "nasabah"
        }, content);
    }
};

export default CoordCustomerPortfolioMobile;
